import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { useState } from "react";
import { H3 } from "@/components/ui/Typography";

interface CreateBudgetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateBudget: (name: string, startMonth: string, duration: "Year" | "Quarter", growth: number) => void;
}

export const CreateBudgetDialog = ({
  open,
  onOpenChange,
  onCreateBudget,
}: CreateBudgetDialogProps) => {
  const [budgetName, setBudgetName] = useState("");
  const [startMonth, setStartMonth] = useState("2025-01");
  const [duration, setDuration] = useState<"Year" | "Quarter">("Year");
  const [growth, setGrowth] = useState("0");

  const handleCreate = () => {
    if (budgetName.trim()) {
      const [year, month] = startMonth.split("-").map(Number);
      const growthNumber = Math.min(Math.max(Number(growth), 0), 40);

      // Calculate end date based on duration
      const monthTo = duration === "Year" ? 12 : month + 2;

      onCreateBudget(budgetName, startMonth, duration, growthNumber);
      setBudgetName("");
      setStartMonth("2025-01");
      setDuration("Year");
      setGrowth("0");
      onOpenChange(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onOpenChange(false)}
      title="Create new budget"
      footer={
        <>
          <Button variant="outlined" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!budgetName.trim()}>
            Create
          </Button>
        </>
      }
    >
      <div className="space-y-6 py-4">
        <div className="space-y-2">
          <Input
            label="Name"
            placeholder="New budget"
            value={budgetName}
            onChange={(e) => setBudgetName(e.target.value)}
          />
        </div>

        <div>
          <H3 className="mb-4">Budget period</H3>
          <div className="grid grid-cols-2 gap-4">
            <Input
              type="month"
              label="Starting month"
              value={startMonth}
              onChange={(e) => setStartMonth(e.target.value)}
            />
            <Select
              label="Duration"
              value={duration}
              onChange={(e) => setDuration(e.target.value as "Year" | "Quarter")}
              options={[
                { value: "Year", label: "Year" },
                { value: "Quarter", label: "Quarter" },
              ]}
            />
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            The created budget is based on the latest available financial data for each individual month.
          </p>
        </div>

        <div>
          <h3 className="text-lg font-medium mb-4">Modify projected revenue</h3>
          <div className="space-y-2">
            <Input
              label="Growth"
              type="number"
              inputProps={{ min: "0", max: "40" }}
              value={growth}
              onChange={(e) => {
                let value = e.target.value;
                if (parseInt(value, 10) > 40) {
                  value = "40"; // Ensure the value does not exceed 40
                }
                setGrowth(value);
              }}
              helperText="Allowed range: 0-40%"
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
};